import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import Modal from "./Modal";

/**
 * Reusable confirmation modal for admin-only destructive actions (void,
 * deactivate, hard-delete). Captures a required reason that flows into the
 * audit trail. Renders nothing when `open` is false.
 *
 * Props:
 *   open                bool        — visibility
 *   title               string      — modal title, e.g. "Void Order PP0518262198"
 *   description         node        — body explaining what's about to happen
 *   confirmLabel        string      — confirm button text (default "Confirm")
 *   tone                'red'|'amber' — visual emphasis (default 'red')
 *   minReasonLength     number      — minimum chars for the reason (default 5)
 *   onCancel            fn
 *   onConfirm           fn(reason)  — async; modal stays open until it resolves
 *   testIdPrefix        string      — for data-testid hooks
 */
export default function AdminActionModal({
    open,
    title,
    description,
    confirmLabel = "Confirm",
    tone = "red",
    minReasonLength = 5,
    onCancel,
    onConfirm,
    testIdPrefix = "admin-action",
}) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    if (!open) return null;

    const trimmed = reason.trim();
    const canConfirm = trimmed.length >= minReasonLength && !busy;

    const handleConfirm = async () => {
        if (!canConfirm) return;
        setBusy(true);
        setError("");
        try {
            await onConfirm(trimmed);
            setReason("");
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.message ||
                "Action failed.";
            setError(typeof msg === "string" ? msg : JSON.stringify(msg));
        } finally {
            setBusy(false);
        }
    };

    const handleCancel = () => {
        if (busy) return;
        setReason("");
        setError("");
        onCancel?.();
    };

    const confirmClass =
        tone === "amber"
            ? "bg-amber-600 hover:bg-amber-700"
            : "bg-red-700 hover:bg-red-800";

    return (
        <Modal title={title} onClose={handleCancel} size="md" testId={`${testIdPrefix}-modal`}>
            <div className="space-y-4">
                <div className="flex gap-3 items-start">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-gray-800 leading-relaxed">
                        {description}
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Reason <span className="text-red-700">*</span>
                    </label>
                    <textarea
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={`Required — explain why (min ${minReasonLength} chars). This goes into the audit trail.`}
                        className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                        data-testid={`${testIdPrefix}-reason`}
                        autoFocus
                    />
                    <div className="text-xs text-gray-500 mt-1">
                        {trimmed.length}/{minReasonLength} characters minimum
                    </div>
                </div>

                {error && (
                    <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={busy}
                        className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        data-testid={`${testIdPrefix}-cancel`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                        className={`odr-btn rounded-sm ${confirmClass} text-white text-sm font-semibold px-4 disabled:opacity-50`}
                        data-testid={`${testIdPrefix}-confirm`}
                    >
                        {busy ? "Working…" : confirmLabel}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
